import { sendOutput, sendSignal } from '@shared/port-send-v1.0.0';
import setItem from './proxy/setItem';
import Node from './proxy/Node';
import shemeFunc from './shemeFunc';
import type { Item } from '@shared/types-v0.1.0';
import type { NoodlNode } from '@shared/node-v1.0.0';
import type { Props } from '../node/definition';
import type { FetchScheme, SchemeData } from '../node/store';

const fetchDataByScheme = async (
	p: Props,
	noodlNode: NoodlNode,
	schemesData: SchemeData[],
	localCache: { [x: string]: any },
	iScheme: FetchScheme,
	iLevel = 0,
	parentItem?: Item
	// parentItem: { [id: string]: string } = {}
): Promise<SchemeData> => {
	// Формируем filters на основе filtersFunc
	if (iScheme.filtersFunc) {
		// Если есть filtersFunc, то мы игнорируем filters
		iScheme.filters = iScheme.filtersFunc(localCache);
	}

	// Получаем документы
	const fetchLocalDocs = R?.localData?.fetchLocalDocs || false;
	const getLocalDocsCount = R?.localData?.getLocalDocsCount || false;

	if (!fetchLocalDocs) {
		R.libs.mantine.MantineError?.('useLocalData Error!', 'Метод fetchLocalDocs не найден!');
		return Promise.reject();
	}

	if (!getLocalDocsCount) {
		R.libs.mantine.MantineError?.('useLocalData Error!', 'Метод getLocalDocsCount не найден!');
		return Promise.reject();
	}

	// Получаем общее число, перед основным запросом, так как может использоваться пагинация
	const totalCount = await getLocalDocsCount({
		dbClass: iScheme.dbClass,
		filters: iScheme.filters || {},
	});

	// Если включена пагинация
	if (p.store.paginationOn && iScheme.dbClass === p.paginationClass) {
		// И проверяем, указан ли у него размер 1 чанка
		// (а он уже должен быть указан, так как мы проверяем это в validation)
		p.store.from = p.store.page && iScheme?.size ? iScheme.size * (p.store.page - 1) : 0;

		if (iScheme.size) {
			// Рассчитываем число страниц
			const currentTotalPagesCount = Math.ceil(totalCount / iScheme.size);

			// Если оно не равно числу страниц в store, то обновляем
			if (p.store.totalPagesCount !== currentTotalPagesCount) {
				p.store.totalPagesCount = currentTotalPagesCount;
			}
		}
	}

	const localItems: Item[] = await fetchLocalDocs({
		dbClass: iScheme.dbClass,
		sorts: iScheme.sorts,
		filters: iScheme.filters || {},
		from: iScheme.dbClass === p.paginationClass ? p.store.from : 0,
		size: iScheme?.size,
	});

	// Если включена подписка, то подписываемся, добавляем подписки в p.subscribes
	if (p.store.subscribe && !p.controlled) {
		// В подписку подаем Promise, а не сами данные! Значит await не нужен!
		// Использовать fetchLocalDocs напрямую не получится, так как она возвращает массив документов, а нам надо коллекцию с фильтрами, для подписки.
		// Значит .toArray() и сотрировку .then((array: any) нужно делать после получения результатов подписки

		// При таком сценарии, мы подписываемся на все изменения коллекиии, и уже потом отфильтровываем!
		// А нам надо подписаться на фильтрованную коллекцию!
		const iSubscriptionQuery = R.localData.liveQuery(() =>
			fetchLocalDocs({
				dbClass: iScheme.dbClass,
				sorts: iScheme.sorts,
				filters: iScheme.filters || {},
				from: iScheme.dbClass === p.paginationClass ? p.store.from : 0,
				size: iScheme?.size,
			})
		);

		// Генерируем хэш, для сравнения изменений
		if (!(p.store.hashForSchemes instanceof Object)) {
			p.store.hashForSchemes = {};
		}
		p.store.hashForSchemes[iScheme.dbClass] = JSON.stringify(localItems);
		p.store.hashForSchemes[`${iScheme.dbClass}_totalCount`] = JSON.stringify(localItems);

		const iSubscription = iSubscriptionQuery.subscribe({
			next: async (result: any) => {
				// Рассчитываем хэш для текущего результата выдачи
				const iSubsHash = JSON.stringify(result);

				// Если мы получили новые данные и хэш отличается, то обновляем его и выдаем новые данные
				if (p.store.hashForSchemes?.[iScheme.dbClass] !== iSubsHash) {
					if (!(p.store.hashForSchemes instanceof Object)) {
						p.store.hashForSchemes = {};
					}
					p.store.hashForSchemes[iScheme.dbClass] = iSubsHash;

					// console.log(
					// 	'R?.localData?.localDataReplicationEngine?.replicationStates?.isProcessing',
					// 	R?.localData?.localDataReplicationEngine?.replicationStates?.isProcessing
					// );
					// Не реагируем, если идет репликация и друг сработала подписка, хотя все репликации делаются в транзакциях.
					// if (R?.localData?.localDataReplicationEngine?.replicationStates?.isProcessing !== true) {
					// console.log('\n\n==========================================');
					// console.log('Сработала подписка', result);
					// При изменении документов по подписке, обновляем R.items и проверяем прогоняем все рекурсии
					// Преобразуем документы в объекты
					const updatedItems = result.map((item: any) => {
						// Деструктурируем объект, выделяя нужные поля
						// Создамдим очищеннный от технических полей объект cleanItem
						const { _deleted, _rev, _meta, _attachments, fixed, ...cleanItem } = item;

						cleanItem.dbClass = iScheme.dbClass;

						// Создадим прокси этих items
						setItem(cleanItem);

						return cleanItem;
					});

					// Обновляем соответствующую схему в schemesData
					const schemeToUpdate = p.store.schemesData.find((fScheme) => fScheme.scheme.dbClass === iScheme.dbClass);

					if (schemeToUpdate) {
						schemeToUpdate.itemIds = updatedItems.map((item: any) => item.id);
						schemeToUpdate.fetched = updatedItems.length;
					}

					// Если это 0 уровень и корневая схема (path === 'root'), обновляем выходные данные
					if (iLevel === 0 && schemeToUpdate?.path === 'root' && p.outputDbClasses?.includes(iScheme.dbClass)) {
						sendOutput(noodlNode, `${iScheme.dbClass}Items`, updatedItems);
						sendOutput(noodlNode, `${iScheme.dbClass}Fetched`, updatedItems.length);
					}
					// }
				}
			},
			error: (error: any) => {
				console.error('Ошибка подписки:', error);
			},
		});

		// На общее число приходится подписываться отдельно, так как при отработке подписки лучше не использовать асинхронные функции
		const iTotalCountSubscriptionQuery = R.localData.liveQuery(() =>
			getLocalDocsCount({
				dbClass: iScheme.dbClass,
				filters: iScheme.filters || {},
			})
		);

		const iTotalCountSubscription = iTotalCountSubscriptionQuery.subscribe({
			next: async (result: any) => {
				// Получаем общее количество, для проверки
				const newTotalCount = result;

				if (p.store.hashForSchemes?.[`${iScheme.dbClass}_totalCount`] !== newTotalCount) {
					if (!(p.store.hashForSchemes instanceof Object)) {
						p.store.hashForSchemes = {};
					}
					p.store.hashForSchemes[`${iScheme.dbClass}_totalCount`] = newTotalCount;

					// Обновляем соответствующую схему в schemesData
					const schemeToUpdate = p.store.schemesData.find((fScheme) => fScheme.scheme.dbClass === iScheme.dbClass);

					if (schemeToUpdate) {
						schemeToUpdate.total = newTotalCount;
					}

					// Если это корневая схема (path === 'root'), обновляем выходные данные
					if (schemeToUpdate?.path === 'root' && p.outputDbClasses?.includes(iScheme.dbClass)) {
						sendOutput(noodlNode, `${iScheme.dbClass}Total`, newTotalCount);
					}
				}
			},
			error: (error: any) => {
				console.error('Ошибка подписки:', error);
			},
		});
		// Добавляем подписки
		p.store.subscribes.push(iSubscription, iTotalCountSubscription);
	}

	// Преобразуем документы в объекты
	const items = localItems.map((item) => {
		// Деструктурируем объект, выделяя нужные поля
		// Создамдим очищеннный от технических полей объект cleanItem
		const { _deleted, _rev, _meta, _attachments, fixed, ...cleanItem } = item;

		cleanItem.dbClass = iScheme.dbClass;

		// Создадим прокси этих items
		setItem(cleanItem);

		return cleanItem;
	});

	// Добавляем объекты с классом в локальный кеш, чтобы их могли использовать другие схемы в filtersFunc
	localCache[iScheme.dbClass] = items;

	const parentScheme = parentItem?.id ? schemesData.find((fScheme) => fScheme.itemIds.includes(parentItem.id)) : false;

	// Формируем массив со всем схемами всех рекурсивных items до вызова дальнейших рекурсий
	const iSchemeData = {
		itemIds: items.map((i: Item) => i.id),
		scheme: iScheme,
		level: iLevel,
		parentId: parentItem?.id,
		path: parentItem?.id && parentScheme ? `${parentItem.id}.${parentScheme.path}` : 'root',
		fetched: items.length,
		total: totalCount,
	};

	schemesData.push(iSchemeData);

	// Если у схемы есть childrenFunc - то получаем детей
	if (iScheme.childrenFunc && iLevel < 1) {
		// Перебираем все items и применяем к каждому по 1 разу саму эту же функцию
		for (const item of items) {
			// Получаем функции для запроса дочерних items
			const childrenSchemes = iScheme.childrenFunc(iLevel + 1, item) || [];
			// Перебираем каждую полученную схему и обрабатываем её
			await Promise.all(
				childrenSchemes.map(async (iChildrenScheme) => {
					return await fetchDataByScheme(p, noodlNode, schemesData, localCache, iChildrenScheme, iLevel + 1, item);
				})
			);
		}
	}

	// console.log('iScheme.recursionFunc && iLevel < 10', iScheme.recursionFunc && iLevel < 10);
	// Если у схемы есть recursionFunc - то получаем потомков
	if (iScheme.recursionFunc && iLevel < 10) {
		// Перебираем все items и применяем к каждому по 1 разу саму эту же функцию
		for (const item of items) {
			// Получаем функции для запроса дочерних items
			const recursionSchemes = iScheme.recursionFunc(iLevel + 1, item) || [];
			// Перебираем каждую полученную схему и обрабатываем её
			await Promise.all(
				recursionSchemes.map(async (iRecursionScheme) => {
					// Добавляем схему рекурсии к каждому уровню
					iRecursionScheme.recursionFunc = iScheme.recursionFunc;
					// console.log('iRecursionScheme:', iRecursionScheme);
					return await fetchDataByScheme(p, noodlNode, schemesData, localCache, iRecursionScheme, iLevel + 1, item);
				})
			);
		}
	}

	return iSchemeData;
};

export const fetchLocalData = async (p: Props, noodlNode: NoodlNode) => {
	sendOutput(noodlNode, 'fetching', true);

	// Итоговый объект с данными по классам
	const localCache: { [dbClass: string]: SchemeData & { items: Item[] } } = {};

	// Массив всех схем, с учетом child и recursion
	const schemesData: SchemeData[] = [];

	if (p.fetchScheme) {
		// Получаем из массива схем - массив массивов, сгруппированных по order
		const fetchSchemeSortedByOrder = shemeFunc.groupSchemesByOrder(p.fetchScheme as FetchScheme[]);

		// Поочерендно обрабатываем каждый order
		for (const iOrderSchemes of fetchSchemeSortedByOrder) {
			// ==Обработка внутри одного ордера==
			if (!iOrderSchemes.length) break;

			// Асинхронно обрабатываем каждую схему
			await Promise.all(
				iOrderSchemes.map(async (iScheme) => {
					const iSchemeData = await fetchDataByScheme(p, noodlNode, schemesData, localCache, iScheme);

					return iSchemeData;
				})
			);
		}

		p.store.schemesData = schemesData;

		// Создаем иерархию
		const flatNodes: Node[] = [];

		// Создаем иерархию
		Node.createHierarchy(p, flatNodes);
		// Создадим прокси нод иерархии или обновим их для реактивности.
		Node.setNodesProxy(p, flatNodes);

		// Дата, выдаваемая на выход
		const data: { [dbClass: string]: SchemeData & { items: Item[] } } = {};

		// В data выдаем только родительские схемы.
		for (const schemeData of p.store.schemesData) {
			if (schemeData.path === 'root') {
				data[schemeData.scheme.dbClass] = {
					...schemeData,
					items: schemeData.itemIds.map((id) => R.items[id]).filter((i) => !!i),
				};

				if (p.outputDbClasses?.includes(schemeData.scheme.dbClass)) {
					sendOutput(
						noodlNode,
						`${schemeData.scheme.dbClass}Items`,
						schemeData.itemIds.map((id) => R.items[id]).filter((i) => !!i)
					);
					sendOutput(noodlNode, `${schemeData.scheme.dbClass}Fetched`, schemeData.fetched);
					sendOutput(noodlNode, `${schemeData.scheme.dbClass}Total`, schemeData.total);
				}
			}
		}

		sendOutput(noodlNode, 'data', data);
		sendOutput(noodlNode, 'rootId', p.store.rootId);
		sendOutput(noodlNode, 'rootNode', R.nodes[p.store.rootId]);
		sendOutput(noodlNode, 'schemesData', p.store.schemesData);
		sendOutput(noodlNode, 'page', p.store.page);
		sendOutput(noodlNode, 'totalPagesCount', p.store.totalPagesCount);
		sendOutput(noodlNode, 'fetching', false);
		sendSignal(noodlNode, 'fetched');
	} else {
		console.log('Отсутствует схема у ', p);
	}
};
